import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { setToastFunction } from './ui/use-toast'
import { useAuth } from '../contexts/AuthContext'
import { useFeatureFlags } from '../contexts/FeatureFlagContext'
import { useSystemHealth } from '../hooks/useSystemHealth'
import { 
  HomeIcon, 
  CogIcon, 
  DocumentTextIcon, 
  BeakerIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  DocumentChartBarIcon,
  UserGroupIcon,
  UserIcon,
  CheckBadgeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  UserPlusIcon,
  QueueListIcon,
  UsersIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  AdjustmentsHorizontalIcon,
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline'

// Import PSG logos
import psgLogo from '../assets/images/paysafego.png'
import psgBrandmarkSmall from '../assets/images/paysafego_brandmark_small.png'
import { isPlatformAdminHostname } from '../config/platformAdminHost'
import PsgAgentWidget from './psgagent/PsgAgentWidget'

const navigationGroups = [
  {
    name: 'Executive',
    items: [
      { name: 'Executive Dashboard', href: '/executive', icon: ChartBarIcon, permission: 'executive/dashboard:view' },
    ]
  },
  {
    name: 'Profile Safe',
    items: [
      // Always visible: dashboard, search, AML — rest under "More profile tools"
      { name: 'Profile Dashboard', href: '/profile-safe/dashboard', icon: HomeIcon, permission: 'profile-safe/dashboard:view' },
      { name: 'Profile Search', href: '/profile-safe/search', icon: MagnifyingGlassIcon, permission: 'profile-safe/search:view' },
      { name: 'AML/PEP Screening', href: '/profile-safe/aml-screening', icon: ShieldExclamationIcon, permission: 'profile-safe/aml-screening:view' },
      { name: 'Profile Onboarding', href: '/profile-safe/onboarding', icon: UserPlusIcon, permission: 'profile-safe/management:edit', advanced: true },
      { name: 'Verification Queue', href: '/profile-safe/verification-queue', icon: QueueListIcon, permission: 'profile-safe/checks:view', advanced: true },
      { name: 'Batch KYC', href: '/profile-safe/batch-kyc', icon: QueueListIcon, permission: 'profile-safe/checks:view', advanced: true },
      { name: 'Visitor Management', href: '/profile-safe/visitors', icon: UsersIcon, permission: 'profile-safe/visitors:view', strictPermission: true },
      { name: 'Profile Configuration', href: '/profile-safe/config', icon: CogIcon, permission: 'profile-safe/management:edit', advanced: true },
      { name: 'Profile Management', href: '/profile-safe/management', icon: UsersIcon, permission: 'profile-safe/management:view', strictPermission: true, advanced: true },
      { name: 'Unassigned Transactions', href: '/profile-safe/issues', icon: QueueListIcon, permission: 'profile-safe/management:view', strictPermission: true, advanced: true },
      { name: 'Onboarding Rules', href: '/profile-safe/onboarding-rules', icon: DocumentTextIcon, permission: 'profile-safe/management:edit', advanced: true },
      { name: 'Profile Checks', href: '/profile-safe/checks', icon: CheckBadgeIcon, permission: 'profile-safe/checks:view', advanced: true },
    ]
  },
  {
    name: 'Monitor Safe',
    items: [
      // Daily ops — rules / builders / analytics under "More monitoring tools"
      { name: 'Monitoring Dashboard', href: '/monitor-safe/dashboard', icon: ChartBarIcon, permission: 'monitor-safe/dashboard:view' },
      { name: 'Monitoring Alerts', href: '/monitor-safe/alerts', icon: ExclamationTriangleIcon, permission: 'monitor-safe/alerts:view' },
      { name: 'Watch List', href: '/monitor-safe/watch-list', icon: ExclamationTriangleIcon, permission: 'monitor-safe/watch-list:view' },
      { name: 'Gaming Data', href: '/monitor-safe/gaming-data', icon: DocumentTextIcon, permission: 'monitor-safe/gaming-data:view' },
      { name: 'Daily Data', href: '/monitor-safe/daily-data', icon: ArrowDownTrayIcon, permissions: ['monitor-safe/daily-data:view', 'monitor-safe/gaming-data:view'] },
      { name: 'Rules Management', href: '/monitor-safe/rules', icon: DocumentTextIcon, permission: 'rules:view', advanced: true },
      { name: 'Rules Builder', href: '/monitor-safe/builder', icon: CogIcon, permissions: ['rules:edit', 'rules:manage', 'monitor-safe/builder:view', 'monitor-safe/builder:edit', 'monitor-safe/builder:manage'], advanced: true },
      { name: 'Rule Scheduler', href: '/monitor-safe/rule-scheduler', icon: ClockIcon, permissions: ['rules:view', 'monitor-safe/rule-scheduler:view', 'monitor-safe/rule-scheduler:edit', 'monitor-safe/rule-scheduler:manage'], advanced: true },
      { name: 'Rule Testing', href: '/monitor-safe/testing', icon: BeakerIcon, permissions: ['rules:view', 'monitor-safe/testing:view', 'monitor-safe/testing:manage'], advanced: true },
      { name: 'Sample Data Generator', href: '/monitor-safe/sample-data', icon: BeakerIcon, permission: 'monitor-safe/sample-data:manage', advanced: true },
      { name: 'Monitoring Analytics', href: '/monitor-safe/analytics', icon: ChartBarIcon, permission: 'monitor-safe/analytics:view', advanced: true },
    ]
  },
  // Compliance Safe: all items listed here (no "More" subsection — unlike Profile / Monitor)
  {
    name: 'Compliance Safe',
    items: [
      { name: "Today's run", href: '/compliance-safe/run', icon: ClipboardDocumentListIcon, permission: 'compliance-safe/dashboard:view' },
      { name: 'Compliance Dashboard', href: '/compliance-safe/dashboard', icon: ChartBarIcon, permission: 'compliance-safe/dashboard:view' },
      { name: 'Investigation', href: '/compliance-safe/investigations', icon: MagnifyingGlassIcon, permission: 'compliance-safe/investigations:view' },
      { name: 'Investigation Pipeline', href: '/compliance-safe/investigations/pipeline', icon: MagnifyingGlassIcon, permission: 'compliance-safe/investigations:view' },
      { name: 'Typology & scoring', href: '/admin/typologies', icon: CogIcon, permission: 'admin/typologies:manage' },
      { name: 'SMR Registry', href: '/compliance-safe/smr-registry', icon: DocumentTextIcon, permission: 'compliance-safe/smr-registry:view' },
      { name: 'TTR Registry', href: '/compliance-safe/ttr-registry', icon: DocumentTextIcon, permission: 'compliance-safe/smr-registry:view' },
      { name: 'ECDD', href: '/compliance-safe/ecdd', icon: DocumentTextIcon, permission: 'ecdd:view' },
      { name: 'Patron Screening Settings', href: '/compliance-safe/settings/patron-screening', icon: CogIcon, permission: 'compliance-safe/patron-screening:view' },
      { name: 'Forms', href: '/compliance-safe/forms', icon: DocumentTextIcon, permission: 'compliance-safe/forms:view' },
      { name: 'Form records', href: '/compliance-safe/forms/records', icon: DocumentTextIcon, permission: 'compliance-safe/forms:view' },
      { name: 'Forms Pipeline', href: '/compliance-safe/forms/pipeline', icon: QueueListIcon, permission: 'compliance-safe/forms:view' },
      { name: 'UAR Reports', href: '/compliance-safe/incidents', icon: ExclamationTriangleIcon, permission: 'compliance-safe/incidents:view' },
      { name: 'Incident Register', href: '/compliance-safe/incident-register', icon: ClipboardDocumentListIcon, permission: 'compliance-safe/incident-register:view' },
      { name: 'Floor alerts', href: '/compliance-safe/floor-alerts', icon: ExclamationTriangleIcon, permission: 'compliance-safe/incident-register:view' },
      {
        name: 'Incident Register Capture',
        href: '/compliance-safe/incident-register/capture',
        icon: ClipboardDocumentListIcon,
        permissions: ['compliance-safe/incident-register:manage', 'mobile/ir:create'],
      },
      {
        name: 'Incident Register config',
        href: '/compliance-safe/incident-register/config',
        icon: CogIcon,
        permission: 'compliance-safe/incident-register:manage',
      },
      { name: 'KYC Registry', href: '/compliance-safe/kyc-registry', icon: DocumentTextIcon, permission: 'profile-safe/kyc-registry:view' },
      { name: 'Compliance reports', href: '/compliance-safe/period-reports', icon: DocumentTextIcon, permission: 'compliance-safe/period-reports:view' },
    ]
  },
  {
    name: 'PayoutSafe',
    items: [
      { name: 'Payout Rules Builder', href: '/payout-safe/builder', icon: CogIcon, permission: 'payout-safe/builder:view' },
      { name: 'Payout Process List', href: '/payout-safe/monitoring', icon: ChartBarIcon, permission: 'payout-safe/monitoring:view' },
      { name: 'Payout Capture', href: '/payout-safe/capture', icon: BanknotesIcon, permission: 'payout-safe/capture:view' },
      // DEPRECATE: remove when platform tenant → Mobile tab is live (see PayoutMobileConfig.jsx)
      { name: 'Mobile Config', href: '/payout-safe/mobile-config', icon: CogIcon, permission: 'payout-safe/mobile-config:view' },
      { name: 'Mobile Simulate', href: '/payout-safe/mobile-simulate', icon: DevicePhoneMobileIcon, permission: 'payout-safe/mobile-simulate:view' },
    ]
  },
  {
    name: 'Governance Safe',
    items: [
      { name: 'Governance Dashboard', href: '/governance-safe/dashboard', icon: ClipboardDocumentListIcon, permission: 'governance-safe/dashboard:view' },
      { name: 'Board Dashboard', href: '/governance-safe/board-dashboard', icon: ChartBarIcon, permission: 'governance-safe/dashboard:view' },
      { name: 'Regulatory Obligations', href: '/governance-safe/obligations', icon: CheckBadgeIcon, permission: 'governance-safe/obligations:view' },
      { name: 'Gaming Rules of Play', href: '/governance-safe/gaming-rules', icon: DocumentTextIcon, permission: 'governance-safe/gaming-rules:view' },
      { name: 'Risk Assessment', href: '/governance-safe/risk', icon: ChartBarIcon, permission: 'governance-safe/risk:view' },
      { name: 'Risk Assessment Configuration', href: '/governance-safe/risk-assessment-config', icon: CogIcon, permission: 'governance-safe/risk-assessment-config:view' },
      { name: 'Risk Register', href: '/governance-safe/risk-register', icon: ClipboardDocumentListIcon, permission: 'governance-safe/risk-register:view' },
      { name: 'Patron Risk Configuration', href: '/governance-safe/risk-config', icon: CogIcon, permission: 'governance-safe/risk:edit' },
      { name: 'Transaction Risk Configuration', href: '/governance-safe/transaction-risk-config', icon: CogIcon, permission: 'governance-safe/risk:edit' },
      { name: 'Approval Policies', href: '/governance-safe/approval-policies', icon: CheckBadgeIcon, permission: 'governance-safe/risk:edit' },
      { name: 'Compliance Checklist', href: '/governance-safe/checklist', icon: DocumentTextIcon, permission: 'governance-safe/checklist:view' },
      { name: 'Training Tracker', href: '/governance-safe/training', icon: UserGroupIcon, permission: 'governance-safe/training:view' },
      { name: 'Tasks', href: '/governance-safe/tasks', icon: ClipboardDocumentListIcon, permission: 'governance-safe/tasks:view' },
    ]
  },
  {
    name: 'System & Master Data',
    items: [
      { name: 'Platform', href: '/admin/platform-config', icon: AdjustmentsHorizontalIcon, permission: 'platform:manage', platformConsoleOnly: true },
      { name: 'Tenants', href: '/admin/tenants', icon: BuildingOfficeIcon, permission: 'platform:manage', platformConsoleOnly: true },
      { name: 'Billable usage', href: '/admin/billable-usage', icon: BanknotesIcon, permission: 'platform:manage', platformConsoleOnly: true },
      { name: 'Venue Configuration', href: '/admin/venue-config', icon: CogIcon, permissions: ['admin/venue-config:view', 'admin/venue-config:manage'] },
      { name: 'Club setup wizard', href: '/admin/club-onboarding', icon: ClipboardDocumentCheckIcon, permissions: ['admin/club-onboarding:view', 'admin/club-onboarding:manage', 'admin/group-config:view', 'admin/group-config:manage'] },
      { name: 'Group Configuration', href: '/admin/group-config', icon: HomeIcon, permissions: ['admin/group-config:view', 'admin/group-config:manage'] },
      { name: 'Form Builder', href: '/admin/form-builder', icon: ClipboardDocumentCheckIcon, permission: 'admin/form-builder:view' },
      { name: 'Employees', href: '/admin/employees', icon: UserGroupIcon, permission: 'admin/employees:view' },
      { name: 'User Management', href: '/admin/user-management', icon: UserGroupIcon, permissions: ['admin/user-management:view', 'admin/user-management:manage'] },
      { name: 'Roles & Permissions', href: '/admin/permissions', icon: CogIcon, permission: 'admin/permissions:view' },
      { name: 'Pending Approvals', href: '/admin/approvals', icon: CheckBadgeIcon, permission: 'admin/approvals:view' },
      { name: 'Daily Data settings', href: '/monitor-safe/daily-data/settings', icon: ArrowDownTrayIcon, permission: 'admin/system:view' },
      { name: 'Activity Logs', href: '/admin/activity', icon: DocumentChartBarIcon, permission: 'admin/activity:view' },
      { name: 'Audit Trail', href: '/admin/audit', icon: ShieldCheckIcon, permission: 'admin/audit:view' },
      { name: 'Third-party usage', href: '/admin/usage', icon: BanknotesIcon, permission: 'admin/billable-usage:view' },
      { name: 'User Profile', href: '/admin/profile', icon: UserIcon, permission: 'admin/profile:view' },
    ]
  }
]

// Flatten navigation for mobile and header title lookup
const navigation = navigationGroups.flatMap(group => group.items)

/** Split `admin/venue-config:manage` correctly (not on every ':'). */
function permissionResourceAction(perm) {
  const i = perm.lastIndexOf(':')
  if (i <= 0) return [perm, 'view']
  return [perm.slice(0, i), perm.slice(i + 1)]
}

/** Sidebar expand keys must match this — "&" in "System & Master Data" is stripped. */
function navGroupKey(name) {
  return String(name || '').replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim()
}

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const SIDEBAR_COLLAPSED_KEY = 'psg_sidebar_collapsed'
  const readSidebarCollapsed = () => {
    try {
      const v = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      if (v != null) return v === 'true'
    } catch { /* private mode */ }
    try {
      return sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  }
  const persistSidebarCollapsed = (next) => {
    const s = String(next)
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, s)
      return
    } catch { /* fall through */ }
    try {
      sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, s)
    } catch { /* ignore */ }
  }
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed)
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev
      persistSidebarCollapsed(next)
      return next
    })
  }, [])
  const [toasts, setToasts] = useState([])
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const userDropdownRef = useRef(null)
  const mainScrollRef = useRef(null)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const { status: systemStatus, responseTime } = useSystemHealth()

  useEffect(() => {
    if (!userDropdownOpen) return
    const onPointerDown = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [userDropdownOpen])

  const [expandedGroups, setExpandedGroups] = useState({
    [navGroupKey('Executive')]: true,
    [navGroupKey('Profile Safe')]: true,
    [navGroupKey('Monitor Safe')]: true,
    [navGroupKey('Compliance Safe')]: true,
    [navGroupKey('PayoutSafe')]: true,
    [navGroupKey('Governance Safe')]: true,
    [navGroupKey('System & Master Data')]: true,
    [navGroupKey('Platform')]: true,
  })
  /** Collapsible "More profile tools" — onboarding, verification, visitors, config (not dashboard/search/AML) */
  const [profileMoreOpen, setProfileMoreOpen] = useState(false)
  /** Collapsible "More monitoring tools" — rules, builders, scheduler, testing, sample data, analytics */
  const [monitorMoreOpen, setMonitorMoreOpen] = useState(false)
  const location = useLocation()
  const { user, logout, can, canExact } = useAuth()
  const { isModuleEnabled, isPageEnabled } = useFeatureFlags()

  const displayUserName =
    (user?.name != null && String(user.name).trim() !== '' ? String(user.name).trim() : '') ||
    user?.username ||
    (typeof user?.sub === 'string' ? user.sub : '') ||
    'User'
  const displayInitial = displayUserName.charAt(0).toUpperCase() || 'U'
  const displayOrgName = React.useMemo(() => {
    if (isPlatformAdminHostname()) return null
    const name = String(user?.group_name || '').trim()
    const id = String(user?.group_id || '').trim()
    if (name) return name
    if (id) return id
    return null
  }, [user?.group_name, user?.group_id])
  const displayOrgId =
    displayOrgName && user?.group_id && displayOrgName !== user.group_id
      ? String(user.group_id).trim()
      : null

  const systemStatusDotClass =
    systemStatus === 'online'
      ? 'bg-green-500'
      : systemStatus === 'degraded'
        ? 'bg-yellow-500'
        : systemStatus === 'offline'
          ? 'bg-red-500'
          : 'bg-gray-400 animate-pulse'

  const systemStatusLabel =
    systemStatus === 'online'
      ? 'System online'
      : systemStatus === 'degraded'
        ? 'System degraded'
        : systemStatus === 'offline'
          ? 'System offline'
          : 'Checking status…'

  const systemStatusTitle = responseTime
    ? `${systemStatusLabel} · ${responseTime}ms`
    : systemStatusLabel

  useEffect(() => {
    const advancedProfilePaths = [
      '/profile-safe/onboarding',
      '/profile-safe/verification-queue',
      '/profile-safe/batch-kyc',
      '/profile-safe/config',
    ]
    if (
      advancedProfilePaths.some(
        (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
      )
    ) {
      setProfileMoreOpen(true)
    }
  }, [location.pathname])

  useEffect(() => {
    const advancedMonitorPaths = [
      '/monitor-safe/rules',
      '/monitor-safe/builder',
      '/monitor-safe/rule-scheduler',
      '/monitor-safe/testing',
      '/monitor-safe/sample-data',
      '/monitor-safe/analytics',
    ]
    const path = location.pathname
    if (
      advancedMonitorPaths.some(
        (p) => path === p || path.startsWith(`${p}/`)
      )
    ) {
      setMonitorMoreOpen(true)
    }
  }, [location.pathname])

  useEffect(() => {
    const el = mainScrollRef.current
    if (!el) return undefined
    el.scrollTop = 0
    setShowScrollTop(false)
  }, [location.pathname])

  useEffect(() => {
    const el = mainScrollRef.current
    if (!el) return undefined
    const onScroll = () => setShowScrollTop(el.scrollTop > 320)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Filter navigation items based on user permissions AND feature flags
  const canSeeItem = (item) => {
    if (item.platformConsoleOnly && !isPlatformAdminHostname()) return false
    // Permission check — single permission or any of item.permissions
    const permList = item.permissions || (item.permission ? [item.permission] : [])
    if (permList.length) {
      const allowed = permList.some((perm) => {
        const [resource, action] = permissionResourceAction(perm)
        return item.strictPermission ? canExact(resource, action) : can(resource, action)
      })
      if (!allowed) return false
    }
    // Feature flag check: gate by route path
    if (item.href && !isPageEnabled(item.href)) return false
    return true
  }

  const visibleGroups = isPlatformAdminHostname()
    ? (() => {
        // The platform-admin app is actually served from frontend-admin/ — this
        // branch only exists for tenant-frontend builds that happen to be hosted
        // on a platform-admin domain (legacy / single-deploy mode). Keep the
        // original flat 'Platform' grouping here; the proper Tenant Onboarding
        // section lives in frontend-admin/src/components/AdminLayout.jsx.
        const items = [
          { name: 'Platform', href: '/admin/platform-config', icon: AdjustmentsHorizontalIcon, permission: 'platform:manage', platformConsoleOnly: true },
          { name: 'Tenants', href: '/admin/tenants', icon: BuildingOfficeIcon, permission: 'platform:manage', platformConsoleOnly: true },
          { name: 'Billable usage', href: '/admin/billable-usage', icon: BanknotesIcon, permission: 'platform:manage', platformConsoleOnly: true },
          { name: 'User Profile', href: '/admin/profile', icon: UserIcon, permission: 'admin/profile:view' },
        ].filter(canSeeItem)
        return items.length ? [{ name: 'Platform', items }] : []
      })()
    : navigationGroups
        .map((group) => {
          const moduleMap = {
            Executive: 'executive',
            'Profile Safe': 'profile-safe',
            'Monitor Safe': 'monitor-safe',
            'Compliance Safe': 'compliance-safe',
            PayoutSafe: 'payout-safe',
            'Governance Safe': 'governance-safe',
            'System & Master Data': 'admin',
          }
          const moduleName = moduleMap[group.name]
          if (moduleName && !isModuleEnabled(moduleName)) return { ...group, items: [] }

          return { ...group, items: group.items.filter(canSeeItem) }
        })
        .filter((group) => group.items.length > 0)

  // Title overrides for routes that don't exist in navigation
  const routeTitleOverrides = {
    '/dashboard': 'Welcome to PaySafeGO',
    '/profile-safe/checks': 'Profile Checks',
    '/profile-safe/checks/aml-pep': 'Profile Checks',
    '/profile-safe/checks/dvs': 'Profile Checks',
    '/profile-safe/onboarding': 'Profile Onboarding',
    '/profile-safe/verification-queue': 'Verification Queue',
    '/profile-safe/batch-kyc': 'Batch KYC',
    '/profile-safe/visitors': 'Visitor Management',
    '/compliance-safe/run': "Today's run",
    '/compliance-safe/dashboard': 'Compliance Dashboard',
    '/compliance-safe/investigations': 'Investigation',
    '/compliance-safe/investigations/pipeline': 'Investigation Pipeline',
    '/governance-safe/risk-assessment-config': 'Risk Assessment Configuration',
    '/admin/typologies': 'Typology & scoring',
    '/compliance-safe/smr-registry': 'SMR Registry',
    '/compliance-safe/ttr-registry': 'TTR Registry',
    '/compliance-safe/ecdd': 'ECDD',
    '/compliance-safe/kyc-registry': 'KYC Registry',
    '/compliance-safe/period-reports': 'Compliance reports',
    '/monitor-safe/watch-list': 'Watch List',
    '/monitor-safe/rules': 'Rules Management',
    '/monitor-safe/rule-scheduler': 'Rule Scheduler',
    '/monitor-safe/sample-data': 'Sample Data Generator',
    '/admin/tenants': 'Tenants',
    '/admin/billable-usage': 'Billable usage',
    '/admin/usage': 'Third-party usage',
    '/payout-safe/mobile-simulate': 'Mobile simulator',
    '/payout-safe/capture': 'Payout Capture',
    '/compliance-safe/floor-alerts': 'Floor alerts',
    '/compliance-safe/incident-register/capture': 'Incident Register Capture',
    '/compliance-safe/incident-register/config': 'Incident Register config',
  }

  const currentTitle = routeTitleOverrides[location.pathname]
    || (location.pathname.startsWith('/compliance-safe/investigations/') && location.pathname !== '/compliance-safe/investigations/pipeline' ? 'Investigation Case' : null)
    || (location.pathname.match(/^\/monitor-safe\/watch-list\/[^/]+$/) ? 'Watchlist Detail' : null)
    || navigation.find(item => item.href === location.pathname)?.name
    || ''

  const navLinkIsActive = (item) => {
    const path = location.pathname
    if (path === item.href) return true
    if (item.href === '/monitor-safe/watch-list' && path.startsWith('/monitor-safe/watch-list/')) return true
    if (item.href === '/monitor-safe/builder' && path.startsWith('/monitor-safe/builder')) return true
    if (item.href === '/monitor-safe/daily-data' && path.startsWith('/monitor-safe/daily-data')) return true
    return false
  }

  const renderNavLink = (item, onMobileClose, collapsed = false) => {
    const isActive = navLinkIsActive(item)
    if (collapsed) {
      return (
        <Link
          key={item.name}
          to={item.href}
          title={item.name}
          className={`group relative flex items-center justify-center w-10 h-10 mx-auto rounded-md ${
            isActive
              ? 'bg-psg-yellow text-psg-teal-900'
              : 'text-gray-300 hover:bg-psg-teal-700 hover:text-white'
          }`}
          onClick={() => onMobileClose?.()}
        >
          <item.icon
            className={`h-5 w-5 flex-shrink-0 ${
              isActive ? 'text-psg-teal-700' : 'text-gray-400 group-hover:text-gray-300'
            }`}
          />
        </Link>
      )
    }
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ml-2 ${
          isActive
            ? 'bg-psg-yellow text-psg-teal-900'
            : 'text-gray-300 hover:bg-psg-teal-700 hover:text-white'
        }`}
        onClick={() => onMobileClose?.()}
      >
        <item.icon
          className={`mr-3 h-6 w-6 flex-shrink-0 ${
            isActive ? 'text-psg-teal-700' : 'text-gray-400 group-hover:text-gray-300'
          }`}
        />
        {item.name}
      </Link>
    )
  }

  const renderGroupNavItems = (group, onMobileClose, collapsed = false) => {
    if (collapsed) {
      return group.items.map((item) => renderNavLink(item, onMobileClose, true))
    }
    if (group.name !== 'Profile Safe' && group.name !== 'Monitor Safe') {
      return group.items.map((item) => renderNavLink(item, onMobileClose))
    }
    const primary = group.items.filter((i) => !i.advanced)
    const advanced = group.items.filter((i) => i.advanced)
    const isProfile = group.name === 'Profile Safe'
    const moreOpen = isProfile ? profileMoreOpen : monitorMoreOpen
    const setMoreOpen = isProfile ? setProfileMoreOpen : setMonitorMoreOpen
    const moreLabel = isProfile ? 'More profile tools' : 'More monitoring tools'
    return (
      <>
        {primary.map((item) => renderNavLink(item, onMobileClose))}
        {advanced.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              className="w-full flex items-center px-2 py-2 text-sm font-medium rounded-md ml-2 text-gray-400 hover:bg-psg-teal-700 hover:text-white"
            >
              <ChevronRightIcon
                className={`mr-2 h-5 w-5 flex-shrink-0 transition-transform duration-200 ${
                  moreOpen ? 'rotate-90' : ''
                }`}
              />
              {moreLabel}
            </button>
            {moreOpen && (
              <div className="space-y-1 pl-1 border-l border-white/20 ml-4 mr-1">
                {advanced.map((item) => renderNavLink(item, onMobileClose))}
              </div>
            )}
          </>
        )}
      </>
    )
  }

  // Setup toast function
  useEffect(() => {
    const showToast = (options) => {
      const id = Date.now()
      const toast = {
        id,
        title: options.title,
        description: options.description,
        variant: options.variant || 'default'
      }
      
      setToasts(prev => [...prev, toast])
      
      // Auto remove after 5 seconds
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, 5000)
    }
    
    setToastFunction(showToast)
  }, [])

  const toggleGroup = (groupName) => {
    const groupKey = navGroupKey(groupName)
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-psg-teal shadow-xl">
          <div className="flex h-16 items-center justify-between px-4">
            <Link to="/dashboard" className="flex flex-col" onClick={() => setSidebarOpen(false)} title="Go to dashboard">
              <img src={psgLogo} alt="PaySafeGO" className="h-10 w-auto object-contain" />
              <div className="text-[10px] text-gray-300/70 font-mono mt-1">
                v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'}
              </div>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-300 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-4">
            {visibleGroups.map((group) => {
              const groupKey = navGroupKey(group.name)
              const isExpanded = expandedGroups[groupKey]
              return (
                <div key={group.name}>
                  <button
                    onClick={() => toggleGroup(group.name)}
                    className="w-full flex items-center justify-between px-2 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider hover:text-white hover:bg-psg-teal-700 rounded-md transition-colors"
                  >
                    <span>{group.name}</span>
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </button>
                  <div className={`mt-2 space-y-1 transition-all duration-200 ease-in-out overflow-hidden ${
                    isExpanded ? 'max-h-[120rem] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    {renderGroupNavItems(group, () => setSidebarOpen(false))}
                  </div>
                </div>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div
        className="hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-200 ease-in-out"
        style={{ width: sidebarCollapsed ? 64 : 256 }}
      >
        <div className="flex flex-col flex-grow bg-psg-teal border-r border-psg-teal-700 pt-5 pb-4 overflow-y-auto overflow-x-hidden">
          <div className="flex flex-shrink-0 px-4">
            {sidebarCollapsed ? (
              <Link to="/dashboard" className="mx-auto" title="Go to dashboard">
                <img
                  src={psgBrandmarkSmall}
                  alt="PaySafeGO"
                  className="h-8 w-8 flex-shrink-0"
                />
              </Link>
            ) : (
              <Link to="/dashboard" className="flex flex-col py-1" title="Go to dashboard">
                <img src={psgLogo} alt="PaySafeGO" className="h-10 w-auto object-contain" />
                <div className="text-[10px] text-gray-300/70 font-mono mt-1">
                  v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'}
                </div>
              </Link>
            )}
          </div>
          {sidebarCollapsed ? (
            <nav className="mt-8 flex-1 flex flex-col items-center space-y-1 px-1">
              {visibleGroups.map((group) => (
                <div key={group.name} className="w-full">
                  <div className="border-t border-white/10 my-2 mx-2" />
                  {renderGroupNavItems(group, null, true)}
                </div>
              ))}
            </nav>
          ) : (
            <nav className="mt-8 flex-1 px-2 space-y-4">
              {visibleGroups.map((group) => {
                const groupKey = navGroupKey(group.name)
                const isExpanded = expandedGroups[groupKey]
                return (
                  <div key={group.name}>
                    <button
                      onClick={() => toggleGroup(group.name)}
                      className="w-full flex items-center justify-between px-2 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider hover:text-white hover:bg-psg-teal-700 rounded-md transition-colors"
                    >
                      <span>{group.name}</span>
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4" />
                      )}
                    </button>
                    <div className={`mt-2 space-y-1 transition-all duration-200 ease-in-out overflow-hidden ${
                      isExpanded ? 'max-h-[120rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      {renderGroupNavItems(group)}
                    </div>
                  </div>
                )
              })}
            </nav>
          )}
          {/* Collapse / expand toggle */}
          <div className="flex-shrink-0 border-t border-white/10 px-2 py-3">
            <button
              onClick={toggleSidebarCollapsed}
              className="w-full flex items-center justify-center gap-2 px-2 py-2 text-xs font-medium text-gray-400 hover:text-white hover:bg-psg-teal-700 rounded-md transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed
                ? <ChevronDoubleRightIcon className="h-4 w-4" />
                : <><ChevronDoubleLeftIcon className="h-4 w-4" /><span>Collapse</span></>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div
        id="psg-main-content"
        className="flex flex-col flex-1 min-h-0 transition-all duration-200 ease-in-out"
      >
        <style>{`@media (min-width: 1024px) { #psg-main-content { padding-left: ${sidebarCollapsed ? 64 : 256}px; } }`}</style>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 bg-white shadow-sm border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-psg-teal lg:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between items-center">
            <div className="flex-1 flex">
              {currentTitle && (
                <h1 className="text-2xl font-semibold text-psg-teal">{currentTitle}</h1>
              )}
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              {/* User dropdown */}
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center gap-2 sm:gap-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-psg-teal px-2 py-1.5 hover:bg-gray-50"
                >
                  {displayOrgName && (
                    <div
                      className="hidden sm:block text-right min-w-0 max-w-[9rem] lg:max-w-[11rem] border-r border-gray-200 pr-3"
                      title={displayOrgId ? `${displayOrgName} (${displayOrgId})` : displayOrgName}
                    >
                      <div className="text-sm font-medium text-gray-800 truncate">{displayOrgName}</div>
                      {displayOrgId && (
                        <div className="text-[11px] text-gray-400 truncate font-mono">{displayOrgId}</div>
                      )}
                    </div>
                  )}
                  <div className="hidden md:block text-right min-w-0 max-w-[12rem]">
                    <div className="text-sm font-medium text-gray-900 truncate">{displayUserName}</div>
                    <div className="text-xs text-gray-500 capitalize truncate">{user?.role}</div>
                  </div>
                  <div className="relative flex-shrink-0" title={systemStatusTitle}>
                    <div className="h-8 w-8 bg-psg-teal rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">{displayInitial}</span>
                    </div>
                    <span
                      className={`absolute top-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${systemStatusDotClass}`}
                      aria-hidden="true"
                    />
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>

                {/* Dropdown menu */}
                {userDropdownOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[60]">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">{displayUserName}</div>
                        <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                        {displayOrgName && (
                          <div className="mt-1 text-xs text-gray-500 truncate" title={displayOrgId || displayOrgName}>
                            {displayOrgName}
                            {displayOrgId ? ` · ${displayOrgId}` : ''}
                          </div>
                        )}
                        <div
                          className="mt-2 flex items-center gap-2 text-xs text-gray-500"
                          title={systemStatusTitle}
                        >
                          <span className={`h-2 w-2 rounded-full flex-shrink-0 ${systemStatusDotClass}`} />
                          <span>{systemStatusLabel}</span>
                          {responseTime ? <span className="text-gray-400">({responseTime}ms)</span> : null}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setUserDropdownOpen(false)
                          logout()
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main ref={mainScrollRef} className="flex-1 min-h-0 overflow-y-auto relative">
          <div className="pt-6 pb-24">
            <div
              className={
                ['/monitor-safe/dashboard', '/governance-safe/board-dashboard', '/compliance-safe/run'].includes(location.pathname) ||
                location.pathname.startsWith('/compliance-safe/ecdd/')
                  ? 'w-full px-4 sm:px-6 lg:px-8'
                  : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
              }
            >
              {children}
            </div>
          </div>
        </main>
        {showScrollTop && (
          <button
            type="button"
            onClick={() => mainScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-psg-teal/80 text-white shadow-md hover:bg-psg-teal focus:outline-none focus:ring-2 focus:ring-psg-teal focus:ring-offset-2"
            aria-label="Jump to top"
            title="Jump to top"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <PsgAgentWidget />

      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden ${
              toast.variant === 'destructive' ? 'border-l-4 border-red-500' : 'border-l-4 border-psg-teal'
            }`}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {toast.title}
                  </p>
                  {toast.description && (
                    <p className="mt-1 text-sm text-gray-500">
                      {toast.description}
                    </p>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                    className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-psg-teal"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Layout
